import {z} from 'zod'
import {EventTypeKeyName} from '../agenda/types'
import i18n from '@/translations/i18n'
import {Access} from './types'
import {PatientAgeRange, UploadedDocument} from '@/types'
import {AssistiveRegimeEnum} from '../projects/types'

// Basic access form schema
export const AccessFormValidationSchema = z.object({
    patient: z.object({
        id: z.string().min(1),
        fullName: z.string()
    }),
    project: z.object({
        id: z.string().min(1),
        code: z.number(),
        startDate: z.string().nullable(),
        expirationDate: z.string().nullable(),
        sector: z.enum(['outpatient', 'domiciliary', 'mixed', 'extended', 'maintenance']),
        patient: z.object({
            serviceType: PatientAgeRange.exclude(['evolutionary_age_adults'])
        }),
        assistiveRegime: AssistiveRegimeEnum.nullish()
    }),
    consultant: z.object({
        id: z.string().min(1),
        fullName: z.string(),
        specializationName: z.string(),
        specializationId: z.string()
    }),
    accessType: z.object({
        id: z.string().min(1),
        accessType: EventTypeKeyName
    }),
    sector: z.enum(['outpatient', 'domiciliary', 'mixed', 'extended', 'maintenance'], {
        required_error: i18n.t('errors:required')
    }),
    accessMode: z.enum(['single_day', 'monthly_summary'], {required_error: i18n.t('errors:required')}),
    date: z.date({
        required_error: i18n.t('errors:required'),
        invalid_type_error: i18n.t('errors:required')
    }),
    time: z.string({required_error: i18n.t('errors:required')}).min(1, {message: i18n.t('errors:required')}),
    isRemote: z.boolean().default(false),
    isARecovery: z.boolean().default(false),
    missedAccess: Access.optional(),
    numberOfAccesses: z.coerce
        .number()
        .min(1, {message: i18n.t('errors:required')})
        .nullish(),
    communicatedOnTime: z.enum(['within_48_hours', 'over_48_hours']).optional(),
    communicatedAttachment: UploadedDocument.nullish()
})
export type AccessFormValidationSchema = z.infer<typeof AccessFormValidationSchema>

// Create access form schema
export const CreateAccessFormValidationSchema = AccessFormValidationSchema.pick({
    patient: true,
    project: true,
    consultant: true,
    accessType: true,
    sector: true,
    accessMode: true,
    date: true
})
    .and(
        z.discriminatedUnion('accessMode', [
            z.object({accessMode: z.literal('single_day')}).merge(
                AccessFormValidationSchema.pick({
                    time: true,
                    isRemote: true,
                    isARecovery: true,
                    missedAccess: true
                })
            ),
            z.object({accessMode: z.literal('monthly_summary')}).merge(
                AccessFormValidationSchema.pick({
                    numberOfAccesses: true
                })
            )
        ])
    )
    .superRefine((data, ctx) => {
        if (data.accessMode === 'single_day' && data.isARecovery && !data.missedAccess) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['missedAccess']
            })
        }
    })
export type CreateAccessFormValidationSchema = z.infer<typeof CreateAccessFormValidationSchema>

// Create absence form schema
export const CreateAbsenceFormValidationSchema = AccessFormValidationSchema.pick({
    patient: true,
    project: true,
    consultant: true,
    accessType: true,
    sector: true,
    date: true,
    communicatedOnTime: true,
    communicatedAttachment: true
})
    .extend({
        numberOfAbsences: z.coerce.number().int().min(1).max(5).default(1)
    })
    .superRefine((data, ctx) => {
        if (data.communicatedOnTime === 'within_48_hours' && !data.communicatedAttachment) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['communicatedAttachment']
            })
        }
    })
export type CreateAbsenceFormValidationSchema = z.infer<typeof CreateAbsenceFormValidationSchema>
